import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  SimpleGrid,
  Stack,
  Text as TextElement,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { TravelExpensesFileUploader } from './FileUploader/TravelExpensesFileUploader';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { defaultValues } from './data/formDefaultValuesExpenses';
import { FormInputs } from './types/formInputsExpenses';
import { TravelExpensesFormValidation } from './validation/validationExpenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { TravelExpensesTableOverview } from './TravelExpensesTableOverview';
import {
  Attachment,
  useExpensesAttachment,
  useExpensesFormMutation,
  useGetPaymentMethods,
  PaymentMethodsQuery,
  useUpdateExpenseById,
  useDeleteAttachment,
  useDeleteExpenseLine,
  TravelExpenseResults,
  useAgentActions,
} from 'api/services/TravelExpenses';
import moment from 'moment';
import { Select, Input, Button, Textarea } from '@landsvirkjun/react.common';
import { AgentActions, TravelRequestResult } from 'api/types/types';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useAuth } from 'components/Auth/AuthProvider';

type TravelExpensesFormProps = {
  fetchExpenses: () => void;
  expenseData: TravelExpenseResults[];
  expensesError: boolean;
  isFetchingExpenses: boolean;
  travelRequestData: TravelRequestResult | undefined;
};

export const TravelExpensesForm = ({
  fetchExpenses,
  expenseData,
  expensesError,
  isFetchingExpenses,
  travelRequestData,
}: TravelExpensesFormProps): JSX.Element => {
  const { hasRoles } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const itemId = queryParams.get('itemId');
  const action = queryParams.get('action');
  const [lineId, setLineId] = useState<string | undefined>('');
  const [documentId, setDocumentId] = useState<string | undefined>('');
  const [actions, setAgentActions] = useState<AgentActions | undefined>(
    travelRequestData?.agentActions
  );
  const adminPath = location?.pathname?.startsWith('/admin/') ? '/admin' : '';
  const hasEditRights = hasRoles('travel-request-edit') as boolean;

  const { data: expenseCategories } = useGetPaymentMethods();

  const [modifiedExpenseCategories, setModifiedExpenseCategories] = useState<
    PaymentMethodsQuery[]
  >([]);
  const [paymentArray, setPaymentArray] = useState<any[]>([]);

  const { mutateAsync: updateExpenseLine, isLoading: isUpdatingExpenseLine } =
    useUpdateExpenseById(id, itemId);

  const { mutateAsync: deleteAttachment, isLoading: isDeletingAttachment } =
    useDeleteAttachment(id, lineId, documentId);

  const { mutateAsync: deleteCostLine } = useDeleteExpenseLine(id, lineId);

  const { mutateAsync: updateAgentActions } = useAgentActions(id);

  const handleDeleteCostLine = async (
    lId: string | undefined
  ): Promise<void> => {
    try {
      await new Promise<void>((resolve) => {
        setLineId(lId);
        resolve();
      });
      await deleteCostLine(undefined);
      fetchExpenses();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAttachment = async (
    docId: string | undefined,
    lId: string
  ): Promise<void> => {
    try {
      await new Promise<void>((resolve) => {
        setDocumentId(docId);
        setLineId(lId);
        resolve();
      });
      await deleteAttachment(undefined);
      fetchExpenses();
    } catch (e) {
      console.error(e);
    }
  };
  const handleActions = async (event: any): Promise<void> => {
    try {
      if (actions) {
        const putActions = {
          [event.target.name]: !actions[event.target.name],
        } as AgentActions;
        const actionResult = await updateAgentActions(putActions);
        setAgentActions(actionResult as AgentActions);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    register,
    trigger,
    formState: { errors },
  } = useForm<FormInputs>({
    // @ts-ignore
    resolver: yupResolver(TravelExpensesFormValidation),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultValues,
    },
  });

  const paymentMethod = watch('paymentMethod');
  const dropzoneFiles = watch('attachments');
  const costType = watch('costType');

  useEffect(() => {
    if (costType) {
      if (!itemId) {
        setValue('paymentMethod', '');
      }
      const filteredArray = modifiedExpenseCategories.filter(
        (item) => item.categoryId.toString() === costType
      );
      const newPaymentArray =
        filteredArray.length > 0 ? filteredArray[0].payment : [];
      setPaymentArray(newPaymentArray);
    }
    // eslint-disable-next-line
  }, [costType, itemId]);

  useEffect(() => {
    if (travelRequestData) {
      setAgentActions(travelRequestData?.agentActions);
    }
    // eslint-disable-next-line
  }, [travelRequestData]);

  useEffect(() => {
    if (expenseCategories) {
      const newArray = expenseCategories
        .map((item) => ({
          categoryId: item.categoryId,
          categoryDescription: item.categoryDescription,
          label: item.categoryDescription,
          value: item.categoryId,
          payment: item.payment.map((paymentItem) => ({
            label: paymentItem.paymentDescription,
            value: paymentItem.paymentMethod,
            ...paymentItem,
          })),
        }))
        .filter((cat) => cat.categoryDescription !== 'Dagpeningar');
      setModifiedExpenseCategories(newArray);
    }
  }, [expenseCategories]);

  useEffect(() => {
    if (itemId && expenseData.length > 0 && action === 'kostnadur') {
      const selectedItem = expenseData.filter((el) => el.id === itemId);
      Object.keys(selectedItem[0]).forEach((fieldName) => {
        if (fieldName === 'categoryId') {
          if (modifiedExpenseCategories) {
            const selectedCategory = modifiedExpenseCategories.find(
              (cat) => cat.categoryId.toString() === selectedItem[0][fieldName]
            );
            if (selectedCategory) {
              setValue('costName', selectedCategory.label);
              setValue('costType', selectedCategory.value.toString());
            }
          }
        }
        if (fieldName === 'paymentMethod') {
          const paymentCategory = modifiedExpenseCategories.find((exp) =>
            exp.payment.find(
              (pay) => pay.paymentMethod === selectedItem[0][fieldName]
            )
          );
          const selectedPaymentMethod = paymentCategory?.payment.find(
            (pay) => pay.paymentMethod === selectedItem[0][fieldName]
          );
          if (selectedPaymentMethod) {
            setValue('paymentMethod', selectedPaymentMethod.paymentMethod);
            setValue(
              'paymentMethodName',
              selectedPaymentMethod.paymentDescription
            );
          }
        }
        if (fieldName === 'comment') {
          setValue('comment', selectedItem[0].comment);
        }
        if (fieldName === 'carbonEmission') {
          if (selectedItem[0].carbonEmission) {
            setValue(
              'carbonEmission',
              selectedItem[0].carbonEmission.toString()
            );
          }
        }
        setValue('amount', selectedItem[0].amount.toString());
      });
    }
    // eslint-disable-next-line
  }, [itemId, expenseData, setValue, action]);

  const { mutateAsync: submitAttachment, isLoading: isLoadingAttachments } =
    useExpensesAttachment(id);

  const { mutateAsync: createExpenseForRequest, isLoading } =
    useExpensesFormMutation(id);

const addAttachmentsToReport = async (
  modifiedFiles: Attachment[],
  attachmentLineId: string | undefined
): Promise<void> => {
    try {
      await Promise.all(
        modifiedFiles.map((file) =>
          submitAttachment({ lineId: attachmentLineId, attachment: file })
        )
      );
      fetchExpenses();
      reset();
      if (itemId)
        navigate(
          `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=kostnadur`,
          { state: { hasActions: true } }
        );
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(
          'Request failed with status code:',
          error.response?.status
        );
      } else {
        console.error('An error occurred:', error);
      }
    }
  };

  const prepareAttachments = async (
    newFiles: Attachment[] | File[],
    costName: string,
    attachmentLineId: string | undefined
  ): Promise<void> => {
    const modifiedFiles: Attachment[] = [];
    await Promise.all(
      newFiles.map(
        (file) =>
          new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = (): void => {
              const base64String = reader.result as string;
              const strippedString = base64String.split(',')[1];

              const fileNameParts = file.name.split('.');
              const fileExtension = fileNameParts[fileNameParts.length - 1];
              const newAttachment = {
                name: `${costName}_${travelRequestData?.erpId}_${file.name}`,
                fileType: fileExtension,
                notes: '',
                fileContents: strippedString,
              };
              modifiedFiles.push(newAttachment);
              resolve();
            };
            reader.readAsDataURL(file);
          })
      )
    );
    await addAttachmentsToReport(modifiedFiles, attachmentLineId);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (
    formData: FormInputs,
    event: React.BaseSyntheticEvent<object, any, any> | undefined
  ) => {
    event?.preventDefault();
    const isValid = await trigger();
    if (isValid) {
      const { attachments, costName, paymentMethodName, ...submitData } =
        formData;
      const dataToSubmit = {
        ...submitData,
        costDate: moment(travelRequestData?.departureDate).format('yyyy-MM-DD'),
        amount: parseInt(formData.amount),
        carbonEmission: formData.carbonEmission
          ? formData.carbonEmission.replace(/,/g, '.')
          : '',
      };
      try {
        const expenseResponse = itemId
          ? await updateExpenseLine(dataToSubmit)
          : await createExpenseForRequest(dataToSubmit);
        const { id: travelRequestId } = expenseResponse as TravelExpenseResults;
        const newLineId = itemId || travelRequestId;
        if (attachments.length > 0) {
          await prepareAttachments(attachments, costName, newLineId);
        } else {
          fetchExpenses();
          reset();
          if (itemId)
            navigate(
              `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=kostnadur`,
              { state: { hasActions: true } }
            );
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(
            'Request failed with status code:',
            error.response?.status
          );
        } else {
          console.error('An error occurred:', error);
        }
      }
    }
  };

  const CommentField = (): JSX.Element => (
    <FormControl>
      <FormLabel>Athugasemdir</FormLabel>
      <Textarea
        placeholder="Aðrar upplýsingar sem þú vilt að komi fram"
        {...register('comment')}
      />
    </FormControl>
  );
  return (
    <Container maxW="container.xl" marginStart="ltr" p={0}>
      {hasEditRights && (
        <Box bg="blue.50" p="3" borderWidth="1px" borderRadius="lg">
          <Stack direction={'row'} gap="3">
            <label>Staða vinnslu:</label>
            {actions?.flight}
            <Checkbox
              name="flight"
              isChecked={actions?.flight}
              onChange={(e): Promise<void> => handleActions(e)}
            >
              Flugbókun
            </Checkbox>
            <Checkbox
              name="hotel"
              isChecked={actions?.hotel}
              onChange={(e): Promise<void> => handleActions(e)}
            >
              Hótelbókun
            </Checkbox>
            <TextElement color={'GrayText'} marginLeft={'20px'}>
              Merkt er við hverja stöðu þegar vinnsluaðili hefur skráð öll gögn
            </TextElement>
          </Stack>
        </Box>
      )}

      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid
          templateColumns={[
            'repeat(1, 1fr)',
            'repeat(1, 1fr)',
            'repeat(1, 1fr)',
            '60% 1fr',
          ]}
          gap={10}
          mt={10}
        >
          <Box>
            <Grid
              templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)' }}
              gap={10}
            >
              <SimpleGrid spacing={5} columns={[1, 1, 1, 2]} mb={5}>
                <Box>
                  <FormControl isInvalid={!!errors.costType}>
                    <FormLabel>Tegund kostnaðar</FormLabel>
                    <Controller
                      control={control}
                      name="costType"
                      render={(): JSX.Element => (
                        <Select
                          onChange={(val: number): void => {
                            reset();
                            setValue('costType', val.toString());
                            const filteredObject =
                              modifiedExpenseCategories.find(
                                (item) => item.categoryId === val
                              );
                            const categoryDescription = filteredObject
                              ? filteredObject.categoryDescription
                              : '';
                            setValue('costName', categoryDescription);
                          }}
                          // @ts-ignore
                          options={modifiedExpenseCategories}
                          placeholder="Leita eftir kostnaðartegund"
                          defaultValue={
                            costType
                              ? {
                                  label: watch('costName'),
                                  value: costType,
                                }
                              : null
                          }
                          error={!!errors.costType}
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors.costType?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl isInvalid={!!errors.paymentMethod}>
                    <FormLabel>Greiðsluhættir</FormLabel>
                    <Controller
                      control={control}
                      name="paymentMethod"
                      render={(): JSX.Element => (
                        <Select
                          disabled={!costType}
                          onChange={(val: string): void => {
                            setValue('paymentMethod', val.toString());
                            const filteredObject =
                              modifiedExpenseCategories.find(
                                (item) =>
                                  item.categoryId.toString() ===
                                  watch('costType')
                              );
                            const filteredPayments =
                              filteredObject?.payment.find(
                                (el) => el.paymentMethod === val
                              );
                            setValue(
                              'paymentMethodName',
                              filteredPayments?.paymentDescription || ''
                            );
                          }}
                          options={paymentArray}
                          defaultValue={
                            paymentMethod
                              ? {
                                  label: watch('paymentMethodName'),
                                  value: paymentMethod,
                                }
                              : null
                          }
                          placeholder={
                            costType
                              ? 'Leita eftir greiðsluhætti'
                              : 'Veldu tegund kostnaðar fyrst'
                          }
                          error={!!errors.paymentMethod}
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors.paymentMethod?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Box>
              </SimpleGrid>
            </Grid>
            {/* {costType === '4230' && (
              <Grid
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  md: 'repeat(1, 1fr)',
                }}
                gap={10}
              >
                <SimpleGrid spacing={5} columns={{ base: 1, md: 2 }} mb={5}>
                  <Box>
                    <FormControl isInvalid={!!errors.country}>
                      <FormLabel>Land</FormLabel>
                      <Input placeholder="Land" {...register('country')} />
                      <FormErrorMessage>
                        {errors.country?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isInvalid={!!errors.city}>
                      <FormLabel>Borg</FormLabel>
                      <Input placeholder="Borg" {...register('city')} />
                      <FormErrorMessage>
                        {errors.city?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                </SimpleGrid>
              </Grid>
            )} */}
            <Grid
              templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)' }}
              gap={10}
            >
              <SimpleGrid spacing={5} columns={[1, 1, 1, 2]}>
                <FormControl isInvalid={!!errors.amount}>
                  <Stack direction="row">
                    <FormLabel mr={1}>Upphæð (ISK)</FormLabel>
                    <Tooltip
                      label="Kostnaður er fylltur út í íslenskum krónum.
                    Ef reikningur er í erlendri mynt ber notanda skylda til að láta
                    skjáskot af kortafærslu í íslenskum krónum fylgja."
                    />
                  </Stack>
                  <Input {...register('amount')} type="number" />
                  <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
                </FormControl>
                {costType === '4211' ? (
                  <FormControl>
                    <Stack direction="row">
                      <FormLabel>Kolefnisjöfnun flugs (CO₂)</FormLabel>
                      <Tooltip label="Upplýsingar um kolefnisjöfnun flugs finnur þú í flugupplýsingum þínum." />
                    </Stack>
                    <Input {...register('carbonEmission')} />
                  </FormControl>
                ) : (
                  <CommentField />
                )}
              </SimpleGrid>
            </Grid>
            {costType === '4211' && (
              <Grid mt={5}>
                <SimpleGrid spacing={5} columns={{ base: 1, md: 2 }}>
                  <CommentField />
                </SimpleGrid>
              </Grid>
            )}
          </Box>
          <Box>
            <FormControl isInvalid={!!errors.attachments}>
              <FormLabel>Viðhengi</FormLabel>
              <TravelExpensesFileUploader
                setValue={setValue}
                files={dropzoneFiles}
              />
              <FormErrorMessage>{errors.attachments?.message}</FormErrorMessage>
            </FormControl>
          </Box>
        </Grid>
        <Stack direction="row" mt={10}>
          {itemId && (
            <Button
              variant="secondary"
              onClick={(): void => {
                reset();
                navigate(
                  `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=kostnadur`,
                  { state: { hasActions: true } }
                );
              }}
            >
              Hætta við
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            isLoading={
              isLoading || isLoadingAttachments || isUpdatingExpenseLine
            }
            loadingText="Augnablik..."
            name="submit-form"
          >
            {itemId ? 'Uppfæra kostnaðarlið' : 'Skrá kostnaðarlið'}
          </Button>
        </Stack>
      </Box>
      <TravelExpensesTableOverview
        data={expenseData
          .filter((el) => el.costType !== '4215')
          .sort((a, b) => {
            const idA = parseInt(a?.id ?? '0', 10);
            const idB = parseInt(b?.id ?? '0', 10);
            return idA - idB;
          })}
        isError={expensesError}
        isFetching={isFetchingExpenses}
        isDeletingAttachment={isDeletingAttachment}
        handleDeleteAttachment={handleDeleteAttachment}
        handleDeleteCostLine={handleDeleteCostLine}
        noDataText="Engir kostnaðarliðir skráðir ennþá"
      />
    </Container>
  );
};
